import dayjs from "dayjs";
import { ResponsiveContainer, LineChart, Line, Tooltip } from "recharts";
import { TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";

const METRICS = [
  { key: "bp_systolic", label: "Blood pressure", unit: "mmHg", series: [["bp_systolic", "#C62828"], ["bp_diastolic", "#EF6C00"]],
    latest: (r) => (r.bp_systolic != null && r.bp_diastolic != null ? `${r.bp_systolic}/${r.bp_diastolic}` : r.bp_systolic) },
  { key: "pulse", label: "Heart rate", unit: "bpm", series: [["pulse", "#8D6E63"]] },
  { key: "respiration", label: "Respiration", unit: "/min", series: [["respiration", "#00695C"]] },
  { key: "temperature", label: "Temperature", unit: "°F", series: [["temperature", "#B28704"]] },
  { key: "o2_saturation", label: "O2 saturation", unit: "%", series: [["o2_saturation", "#1565C0"]] },
  { key: "weight_lbs", label: "Weight", unit: "lbs", series: [["weight_lbs", "#5E35B1"]] },
];

function Delta({ points, k }) {
  if (points.length < 2) return null;
  const cur = points[points.length - 1][k];
  const prev = points[points.length - 2][k];
  if (cur == null || prev == null) return null;
  const diff = Math.round((cur - prev) * 10) / 10;
  const I = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  return (
    <span className="flex items-center gap-0.5 rounded-full bg-[#F5F2EB] px-1.5 py-0.5 text-[10px] font-bold text-[#6D4C41]" title="Change vs previous reading">
      <I size={11} /> {diff > 0 ? `+${diff}` : diff}
    </span>
  );
}

export function VitalsTrend({ rows }) {
  const chrono = [...rows].reverse().map((r) => ({ ...r, d: dayjs(r.date).format("MMM D") }));
  const cards = METRICS.map((m) => {
    const points = chrono.filter((r) => r[m.series[0][0]] != null && r[m.series[0][0]] !== "").slice(-10);
    return { ...m, points };
  }).filter((m) => m.points.length > 0);
  if (cards.length === 0) return null;
  const n = Math.max(...cards.map((c) => c.points.length));
  return (
    <div className="mb-4 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3" data-testid="cf-vitals-trend">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold text-[#5D4037]">
        <Activity size={13} className="text-[#7B6157]" /> Vitals trend
        <span className="font-normal text-[#84675B]">last {n} reading{n === 1 ? "" : "s"} for this client</span>
      </div>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
        {cards.map((m) => {
          const last = m.points[m.points.length - 1];
          const latest = m.latest ? m.latest(last) : last[m.series[0][0]];
          return (
            <div key={m.key} className="rounded-lg border border-[#F1EEE7] bg-white px-2.5 py-2" data-testid={`vitals-trend-${m.key}`}>
              <div className="flex items-center justify-between gap-1">
                <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-[#84675B]">{m.label}</span>
                <Delta points={m.points} k={m.series[0][0]} />
              </div>
              <div className="text-sm font-bold text-[#3E2723]">{latest} <span className="text-[10px] font-medium text-[#84675B]">{m.unit}</span></div>
              {m.points.length >= 2 ? (
                <div className="mt-1 h-9">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={m.points} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #EFEBE0", padding: "4px 8px" }}
                        labelFormatter={(_, p) => (p && p[0] ? p[0].payload.d : "")} />
                      {m.series.map(([k, color]) => (
                        <Line key={k} type="monotone" dataKey={k} stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="mt-1 text-[10px] text-[#84675B]">Only one reading on file</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
